import React, { Component } from 'react'
import styled from 'styled-components'
import GameService from '../../services/game'
import PuzzleTable from './puzzleTable'
import Upload from '../upload/upload'
import Loading from '../loading'
import Modal from '../modal'
import './newGame.scss'

const StyledModal = styled(Modal)`
  width: 100%;
`
const StyledTable = styled(PuzzleTable)`
  width: 90%;
  margin: 0 auto;
`
const TableBox = styled.div`
  height: 420px;
  overflow: auto;
`
const PageButtons = styled.div`
  display: flex;
  flex-direction: row;
  width: 50%;
  margin: 20px auto 0;
  text-align: center;
  align-items: center;
  
  > div {
    flex: 1;
    font-size: 1.4em;
    color: #333;
  }

  > div.page-btn {
    font-size: 2em;
    cursor: pointer;

    &[disabled] {
      visibility: hidden;
      cursor: initial;
    }
  }
`
const MissingText = styled.p`
  display: flex;
  margin: 0;
  height: 100%;
  align-items: center;
  justify-self: center;
`

const PreviewBackdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 20;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  box-sizing: border-box;
`

const PreviewCard = styled.div`
  width: 100%;
  max-width: 900px;
  max-height: 82vh;
  overflow: auto;
  border-radius: 12px;
  background-color: #d6f0df;
  padding: 14px;
  box-sizing: border-box;
`

const PreviewTitle = styled.h2`
  margin: 0;
  color: #2f4538;
`

const PreviewMeta = styled.p`
  margin: 4px 0 10px;
  color: #2f4538;
  font-size: 0.8em;
`

const PreviewHeader = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 10px;
  margin-bottom: 6px;
`

const PreviewHeaderText = styled.div`
  grid-column: 2;
  min-width: 0;
  text-align: center;
`

const HeaderButton = styled.button`
  border-radius: 8px;
  border: 1px solid #2f4538;
  padding: 8px 12px;
  background-color: rgba(255, 255, 255, 0.6);
  color: #2f4538;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
  justify-self: start;

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`

const TopStartButton = styled(HeaderButton)`
  justify-self: end;
  border-radius: 8px;
  border: none;
  background-color: #78b08f;
  color: #333;
`

const PreviewLoadingBox = styled.div`
  min-height: 180px;
  display: flex;
  align-items: center;
  justify-content: center;
`

const PreviewContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const GridScroller = styled.div`
  overflow-x: auto;
`

const GridWrap = styled.div`
  display: inline-block;
  background-color: #fff;
`

const GridRow = styled.div`
  display: flex;
`

const GridCell = styled.div`
  width: 20px;
  height: 20px;
  border-top: 1px solid #2f4538;
  border-left: 1px solid #2f4538;
  background-color: ${({ $blocked }) => ($blocked ? '#2f4538' : '#fff')};
  border-right: ${({ $lastCol }) => ($lastCol ? '1px solid #2f4538' : 'none')};
  border-bottom: ${({ $lastRow }) => ($lastRow ? '1px solid #2f4538' : 'none')};
  color: #2f4538;
  font-size: 0.45em;
  font-weight: 700;
  padding-left: 1px;
  padding-top: 1px;
  box-sizing: border-box;
`

const ClueSection = styled.div`
  background-color: rgba(255, 255, 255, 0.45);
  border-radius: 8px;
  padding: 10px;
  text-align: left;
`

const ClueHeader = styled.h3`
  margin: 0 0 6px;
  color: #2f4538;
  font-size: 1.1em;
`

const ClueText = styled.p`
  margin: 0 0 3px;
  color: #333;
  font-size: 0.82em;
  line-height: 1.3;
`

const PreviewButtons = styled.div`
  margin-top: 10px;
  display: flex;
  gap: 10px;
`

const SecondaryButton = styled.button`
  flex: 1;
  border-radius: 8px;
  border: 1px solid #2f4538;
  padding: 10px;
  background-color: rgba(255, 255, 255, 0.6);
  color: #2f4538;
  font-weight: 700;
  cursor: pointer;
`

const StartButton = styled.button`
  flex: 1;
  border-radius: 8px;
  border: none;
  padding: 10px;
  background-color: #78b08f;
  color: #333;
  font-weight: 700;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`

const SOURCE_MAP = {
  atl: 'Atlantic',
  club: 'Crossword Club',
  db: 'The Daily Beast',
  pop: 'Daily Pop',
  std: 'Der Standard',
  tgam: 'The Globe And Mail cryptic',
  grdc: 'Guardian Cryptic',
  grde: 'Guardian Everyman',
  grdp: 'Guardian Prize',
  grdq: 'Guardian Quick',
  grdu: 'Guardian Quiptic',
  grds: 'Guardian Speedy',
  grdw: 'Guardian Weekend',
  lat: 'Los Angeles Times',
  mck: 'The McKinsey Crossword',
  nyt: 'New York Times',
  nytm: 'New York Times Mini',
  nytv: 'New York Times Variety',
  tny: 'The New Yorker',
  nd: 'Newsday',
  pzm: 'Puzzmo',
  sdp: 'Simply Daily Puzzles',
  sdpc: 'Simply Daily Puzzles Cryptic',
  sdpq: 'Simply Daily Puzzles Quick',
  uni: 'Universal',
  usa: 'USA Today',
  vox: 'Vox',
  wp: 'Washington Post',
}

const getDisplayTitle = (title = '') => title ? title.replace(/\s*\([^)]*\)\s*$/, '') : 'Untitled'

const getSizeLabel = (puzzle) => (puzzle?.width && puzzle?.height) ? `${puzzle.width}x${puzzle.height}` : '-'

const sortClueEntries = (clues = {}) =>
  Object.entries(clues).sort(([a], [b]) => Number(a) - Number(b))

const buildClueLookup = (across = {}, down = {}) => {
  const lookup = {}
  const assign = (entries) => {
    Object.entries(entries).forEach(([number, clue]) => {
      const key = `${clue.row}-${clue.col}`
      if (!lookup[key] || Number(number) < Number(lookup[key])) {
        lookup[key] = number
      }
    })
  }
  assign(across)
  assign(down)
  return lookup
}

export class NewGame extends Component {
  constructor (props) {
    super(props)

    this.state = {
      files: [],
      page: 1,
      loading: true,
      startingFilename: null,
      previewVisible: false,
      previewFile: null,
      previewLoading: false,
      previewData: null,
      previewCache: {},
    }
    this.loadFiles()
  }

  loadFiles = () => {
    const { page } = this.state
    const userToken = localStorage.getItem('userToken')
    GameService.getPuzzles(page, userToken)
      .then(files => {
        this.setState({ files })
      })
      .finally(() => {
        this.setState({ loading: false })
      })
  }

  handleNextPage = () => {
    this.setState(
      prevState => ({ page: prevState.page + 1, loading: true }),
      this.loadFiles,
    )
  }

  handlePreviousPage = () => {
    this.setState(
      prevState => ({ page: Math.max(prevState.page - 1, 1), loading: true }),
      this.loadFiles,
    )
  }

  onStart = (filename) => {
    const { history } = this.props
    this.setState({ startingFilename: filename })
    GameService.createGame(filename)
      .then(game => {
        history.push(`/game/${game.short_id}`)
      })
      .catch(console.error)
      .finally(() => {
        this.setState({ startingFilename: null })
      })
  }

  onPreview = (file) => {
    this.setState({
      previewFile: file,
      previewVisible: true,
    })

    const { previewCache } = this.state
    const cached = previewCache[file.filename]
    if (cached) {
      this.setState({
        previewLoading: false,
        previewData: cached,
      })
      return
    }

    this.setState({
      previewLoading: true,
      previewData: null,
    })

    GameService.getPuzzlePreview(file.filename)
      .then(payload => {
        this.setState(prevState => ({
          previewData: payload,
          previewCache: { ...prevState.previewCache, [file.filename]: payload },
        }))
      })
      .catch((error) => {
        console.error('Failed to load puzzle preview', error)
        this.setState({ previewData: null })
      })
      .finally(() => {
        this.setState({ previewLoading: false })
      })
  }

  closePreview = () => {
    const { previewLoading, startingFilename } = this.state
    if (previewLoading || startingFilename) {
      return
    }
    this.setState({ previewVisible: false })
  }

  renderPreviewModal = () => {
    const {
      previewVisible,
      previewFile,
      previewLoading,
      previewData,
      startingFilename,
    } = this.state

    if (!previewVisible) {
      return null
    }

    const clueNumberLookup = previewData
      ? buildClueLookup(previewData.across || {}, previewData.down || {})
      : {}
    const acrossEntries = previewData ? sortClueEntries(previewData.across || {}) : []
    const downEntries = previewData ? sortClueEntries(previewData.down || {}) : []
    const startDisabled = !previewFile || Boolean(startingFilename) || previewLoading
    const startLabel = startingFilename === previewFile?.filename ? 'Starting...' : 'Start Puzzle'
    const startCurrentPuzzle = () => {
      if (!previewFile) {
        return
      }
      this.setState({ previewVisible: false }, () => this.onStart(previewFile.filename))
    }

    return (
      <PreviewBackdrop onClick={this.closePreview}>
        <PreviewCard onClick={(e) => e.stopPropagation()}>
          <PreviewHeader>
            <HeaderButton onClick={this.closePreview}>Keep Looking</HeaderButton>
            <PreviewHeaderText>
              <PreviewTitle>{getDisplayTitle(previewFile?.title || previewData?.title || '')}</PreviewTitle>
              <PreviewMeta>
                {previewFile ? `${getSizeLabel(previewFile)} • ${SOURCE_MAP[previewFile.source || ''] || previewFile.source || '-'}` : ''}
              </PreviewMeta>
            </PreviewHeaderText>
            <TopStartButton
              disabled={startDisabled}
              onClick={startCurrentPuzzle}>
              {startLabel}
            </TopStartButton>
          </PreviewHeader>

          {previewLoading ? (
            <PreviewLoadingBox>
              <Loading />
            </PreviewLoadingBox>
          ) : previewData ? (
            <PreviewContent>
              <GridScroller>
                <GridWrap>
                  {Array.from({ length: previewData.height }).map((_, row) => (
                    <GridRow key={`row-${row}`}>
                      {Array.from({ length: previewData.width }).map((__, col) => {
                        const idx = (row * previewData.width) + col
                        const blocked = previewData.board[idx] === '.'
                        const clueNumber = clueNumberLookup[`${row}-${col}`] || ''
                        return (
                          <GridCell
                            key={`${row}-${col}`}
                            $blocked={blocked}
                            $lastCol={col === previewData.width - 1}
                            $lastRow={row === previewData.height - 1}>
                            {!blocked ? clueNumber : ''}
                          </GridCell>
                        )
                      })}
                    </GridRow>
                  ))}
                </GridWrap>
              </GridScroller>
              <ClueSection>
                <ClueHeader>Across</ClueHeader>
                {acrossEntries.map(([number, clue]) => (
                  <ClueText key={`a-${number}`}>{number}. {clue.clue}</ClueText>
                ))}
              </ClueSection>
              <ClueSection>
                <ClueHeader>Down</ClueHeader>
                {downEntries.map(([number, clue]) => (
                  <ClueText key={`d-${number}`}>{number}. {clue.clue}</ClueText>
                ))}
              </ClueSection>
            </PreviewContent>
          ) : (
            <PreviewLoadingBox>
              <MissingText>Could not load preview</MissingText>
            </PreviewLoadingBox>
          )}
          <PreviewButtons>
            <SecondaryButton onClick={this.closePreview}>Keep Looking</SecondaryButton>
            <StartButton
              disabled={startDisabled}
              onClick={startCurrentPuzzle}>
              {startLabel}
            </StartButton>
          </PreviewButtons>
        </PreviewCard>
      </PreviewBackdrop>
    )
  }

  render() {
    const { files, page, loading, startingFilename } = this.state
    return (
      <StyledModal
        title='Start new game'>
        <div className='NewGame'>
          <TableBox>
            {loading && <Loading />}
            {!loading && files && files.length > 0 && (
              <StyledTable
                files={files}
                onPreview={this.onPreview}
                actionsDisabled={Boolean(startingFilename)} />
            )}
            {!loading && !files?.length && (
              <MissingText>Unable to load puzzles :(</MissingText>
            )}
          </TableBox>
          {files?.length > 0 && (
            <PageButtons>
              <div className='page-btn' onClick={this.handlePreviousPage} disabled={page === 1}>&lt;</div>
              <div>{page}</div>
              <div className='page-btn' onClick={this.handleNextPage} disabled={files.length < 10}>&gt;</div>
            </PageButtons>
          )}
          <Upload />
        </div>
        {this.renderPreviewModal()}
      </StyledModal>

    )
  }
}

export default NewGame
